import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { exportToCsv } from '@/lib/exportCsv';

const fmt = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const Stat = ({ label, value, testid, strong }) => (
  <div className="min-w-[130px]">
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`text-sm tabular-nums ${strong ? 'font-bold' : 'font-semibold'} ${strong && value < 0 ? 'text-rose-600' : ''}`} data-testid={testid}>{fmt(value)}</div>
  </div>
);

// One accounting definition for every report: posted General Ledger. Operational figure shown alongside for reconciliation.
export function GlAccountingStrip({ startDate, endDate, branchIds = [], mode = 'pnl', operational, operationalLabel }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [showBranches, setShowBranches] = useState(false);
  const key = `${startDate}|${endDate}|${branchIds.join(',')}`;
  useEffect(() => {
    let alive = true;
    const params = { start_date: startDate || '1900-01-01', end_date: endDate || undefined };
    if (branchIds.length === 1) params.branch_id = branchIds[0];
    else if (branchIds.length > 1) params.branch_ids = branchIds.join(',');
    setErr(false);
    api.get('/accounting/gl-summary', { params }).then((r) => alive && setD(r.data)).catch(() => alive && (setD(null), setErr(true)));
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="text-[11px] text-muted-foreground" data-testid="gl-strip-status">{err ? 'Accounting (GL) figures unavailable' : 'Loading accounting (GL) figures…'}</div>;
  const p = d.pnl.total;
  const c = d.cash;
  const glValue = mode === 'cash' ? c.net_movement : p.net_profit;
  const hasOp = typeof operational === 'number';
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid={`gl-strip-${mode}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold">Accounting figures — posted General Ledger</span>
        <span className="text-[10px] text-muted-foreground">authoritative · same definition on P&amp;L, Branch Summary, Cash Flow and Dashboard</span>
        {mode === 'pnl' && d.pnl.by_branch.length > 0 && (
          <Button size="sm" variant="ghost" className="ml-auto h-6 text-[11px]" onClick={() => setShowBranches((s) => !s)} data-testid="gl-strip-branches-toggle">
            {showBranches ? 'Hide' : 'Show'} by branch
          </Button>
        )}
      </div>
      {mode === 'pnl' ? (
        <div className="flex gap-4 flex-wrap">
          <Stat label="GL revenue" value={p.revenue} testid="gl-revenue" />
          <Stat label="GL cost of goods" value={p.cogs} testid="gl-cogs" />
          <Stat label="GL operating expenses" value={p.operating_expenses} testid="gl-opex" />
          <Stat label="GL net profit" value={p.net_profit} testid="gl-net-profit" strong />
          {hasOp && <Stat label={operationalLabel || 'Operational (drill-down)'} value={operational} testid="gl-operational" />}
          {hasOp && <Stat label="Operational − GL" value={operational - glValue} testid="gl-difference" />}
        </div>
      ) : (
        <div className="flex gap-4 flex-wrap">
          <Stat label="Opening cash + bank (GL)" value={c.opening} testid="gl-cash-opening" />
          <Stat label="Net movement" value={c.net_movement} testid="gl-cash-movement" />
          <Stat label="Closing cash + bank (GL)" value={c.closing} testid="gl-cash-closing" strong />
          <Stat label="Internal transfers excluded" value={c.internal_transfers_excluded} testid="gl-cash-transfers" />
          {hasOp && <Stat label={operationalLabel || 'Operational (drill-down)'} value={operational} testid="gl-operational" />}
          {hasOp && <Stat label="Operational − GL" value={operational - glValue} testid="gl-difference" />}
          <span className={`text-[11px] self-end ${c.reconciles ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="gl-cash-reconciles">
            {c.reconciles ? 'Opening + movement = closing' : 'Does not reconcile'}
          </span>
        </div>
      )}
      {mode === 'cash' && (
        <div className="text-[11px] text-muted-foreground" data-testid="gl-cash-accounts">
          {c.accounts.map((a) => `${a.code} ${a.name}: ${fmt(a.closing)}`).join(' · ')}
        </div>
      )}
      {showBranches && mode === 'pnl' && <GlBranchTable rows={d.pnl.by_branch} period={`${startDate || 'all'}_${endDate || ''}`} />}
    </div>
  );
}

function GlBranchTable({ rows, period }) {
  const cols = [
    { key: 'branch_name', label: 'Branch' }, { key: 'revenue', label: 'Revenue' }, { key: 'cogs', label: 'COGS' },
    { key: 'operating_expenses', label: 'Operating expenses' }, { key: 'net_profit', label: 'Net profit' },
  ];
  return (
    <div className="space-y-1">
      <div className="overflow-x-auto border rounded-md bg-background">
        <table className="w-full text-xs" data-testid="gl-branch-table">
          <thead className="bg-muted"><tr>{cols.map((c) => <th key={c.key} className="text-left px-2 py-1">{c.label}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.branch_id || 'unassigned'} className="border-t">
              {cols.map((c) => <td key={c.key} className="px-2 py-1 tabular-nums">{c.key === 'branch_name' ? r[c.key] : fmt(r[c.key])}</td>)}
            </tr>
          ))}</tbody>
        </table>
      </div>
      <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => exportToCsv(`gl_profit_by_branch_${period}.csv`, rows, cols)} data-testid="gl-branch-export-btn">Export CSV</Button>
    </div>
  );
}
